import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Mail } from 'lucide-react';

interface PreviewMessage {
  key: string;
  label: string;
  subject: string;
  html: string;
}

interface Props {
  onClose: () => void;
}

// Modal that previews the exact owner-branded emails guests receive. On open it
// fetches /api/portal/manage/message-previews and shows one tab per message type,
// each rendering the email subject + the real HTML inside an iframe (srcDoc) so the
// styling appears exactly as the guest sees it in their inbox.
export default function GuestMessagesPreview({ onClose }: Props) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<PreviewMessage[] | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/portal/manage/message-previews', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('fetch failed'))))
      .then((data) => {
        if (cancelled) return;
        const list = (data as { messages: PreviewMessage[] }).messages;
        setMessages(list);
        setActiveKey(list[0]?.key ?? null);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const active = messages?.find((m) => m.key === activeKey) ?? null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[92vh] sm:h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-base sm:text-lg truncate">
                {t('portal.manage.messages.preview.title', 'Vista previa de los mensajes al huésped')}
              </h3>
              <p className="text-xs text-slate-500 truncate">
                {t(
                  'portal.manage.messages.preview.subtitle',
                  'Así ve tu huésped los emails, con tu marca',
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg shrink-0"
            aria-label={t('portal.manage.messages.preview.close', 'Cerrar')}
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        {error ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <p className="text-slate-900 font-medium">
              {t('portal.manage.messages.preview.errorTitle', 'No se pudo cargar la vista previa')}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {t('portal.manage.messages.preview.errorSub', 'Inténtalo de nuevo en unos segundos.')}
            </p>
          </div>
        ) : !messages ? (
          <div className="flex-1 flex justify-center items-center">
            <Loader2 className="animate-spin text-teal-500" size={32} />
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="px-3 sm:px-4 pt-3 border-b border-slate-200 shrink-0 overflow-x-auto">
              <div className="flex gap-1.5 min-w-max pb-px">
                {messages.map((m) => {
                  const isActive = m.key === activeKey;
                  return (
                    <button
                      key={m.key}
                      onClick={() => setActiveKey(m.key)}
                      className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors border-b-2 ${
                        isActive
                          ? 'text-teal-700 border-teal-600 bg-teal-50/50'
                          : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subject + rendered email */}
            {active && (
              <div className="flex-1 flex flex-col min-h-0 bg-slate-100">
                <div className="px-5 sm:px-6 py-3 bg-white border-b border-slate-200 shrink-0">
                  <span className="text-xs text-slate-400 uppercase tracking-wide">
                    {t('portal.manage.messages.preview.subjectLabel', 'Asunto')}
                  </span>
                  <p className="text-sm font-semibold text-slate-800 mt-0.5 break-words">
                    {active.subject}
                  </p>
                </div>
                <iframe
                  key={active.key}
                  srcDoc={active.html}
                  title={active.label}
                  className="flex-1 w-full border-0 bg-slate-100"
                  sandbox=""
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
